/**
 * LumeneLogo — Composant SVG inline
 *
 * Le logo est entièrement en SVG pour :
 *   - Rendu parfait à toutes densités d'écran (Retina, OLED)
 *   - Animation Framer Motion directe sur l'accent pendant isScanning
 *   - Pas de fichier image à maintenir
 *   - Accessibilité native (role img + aria-label)
 *
 * Props :
 *   isScanning {boolean}  — anime l'accent en pulsation cuivrée
 *   variant    {string}   — 'dark' (défaut, fond sombre) | 'light' (fond clair)
 *   showBaseline {boolean} — affiche "L'ART À VOIX BASSE" (défaut false)
 *   size       {string}   — 'sm' | 'md' (défaut) | 'lg'
 */

import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'

// ─── Constantes de la marque ───────────────────────────────────────────────
const COPPER    = '#B08D5B'
const NIGHT     = '#1A1D24'
const OFF_WHITE = '#F8F4EC'

// ─── Tailles disponibles ───────────────────────────────────────────────────
const SIZES = {
  sm: { width: 120, height: 40,  fontSize: 16, accentW: 13, accentH: 3.5, accentX: 67,  accentY: 5,  accentOffsetX: 30, rx: 20  },
  md: { width: 160, height: 52,  fontSize: 22, accentW: 17, accentH: 4.5, accentX: 90,  accentY: 6,  accentOffsetX: 40, rx: 26 },
  lg: { width: 220, height: 70,  fontSize: 30, accentW: 23, accentH: 6,   accentX: 123, accentY: 8,  accentOffsetX: 54, rx: 35 },
}

const LumeneLogo = ({
  isScanning   = false,
  variant      = 'dark',
  showBaseline = false,
  size         = 'md',
}) => {
  const s = SIZES[size] || SIZES.md
  const prefersReduced = useReducedMotion()

  const bgColor       = variant === 'dark'
    ? 'rgba(26,29,36,0.85)'
    : 'rgba(248,244,236,0.92)'
  const wordmarkColor = variant === 'dark' ? OFF_WHITE : NIGHT
  const baselineColor = variant === 'dark'
    ? 'rgba(248,244,236,0.7)'
    : 'rgba(26,29,36,0.6)'

  const totalHeight = showBaseline ? s.height + 20 : s.height

  return (
    <svg
      role="img"
      aria-label="Lumène — L'art à voix basse"
      xmlns="http://www.w3.org/2000/svg"
      width={s.width}
      height={totalHeight}
      viewBox={`0 0 ${s.width} ${totalHeight}`}
      overflow="visible"
    >
      {/* Fond rectangulaire arrondi semi-opaque */}
      <rect
        x={0} y={0}
        width={s.width} height={s.height}
        rx={s.rx}
        fill={bgColor}
      />

      {/* ── Accent grave ──────────────────────────────────────────────────
          Positionné au-dessus du "e" de Lumene.
          Rotation 22° sens horaire = accent grave (descend gauche→droite).
          Animé en pulsation pendant isScanning.
      ─────────────────────────────────────────────────────────────────── */}
      <motion.rect
        x={s.accentX}
        y={s.accentY}
        width={s.accentW}
        height={s.accentH}
        rx={1.5}
        fill={COPPER}
        transform={`rotate(22, ${s.accentX + s.accentW / 2}, ${s.accentY + s.accentH / 2})`}
        animate={isScanning && !prefersReduced ? { opacity: [1, 0.35, 1] } : { opacity: 1 }}
        transition={isScanning && !prefersReduced
          ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
          : { duration: 0.3 }}
      />

      {/* ── Wordmark "Lumene" ─────────────────────────────────────────────
          Sans accent typographique — l'accent est porté par l'élément
          graphique ci-dessus. C'est la règle de construction du logo.
      ─────────────────────────────────────────────────────────────────── */}
      <text
        x={s.width / 2}
        y={s.height / 2 + s.fontSize * 0.36}
        textAnchor="middle"
        fontFamily="'Atkinson Hyperlegible', system-ui, sans-serif"
        fontSize={s.fontSize}
        fontWeight={400}
        fill={wordmarkColor}
        letterSpacing={0.5}
      >
        Lumene
      </text>

      {/* ── Baseline optionnelle ──────────────────────────────────────── */}
      {showBaseline && (
        <text
          x={s.width / 2}
          y={s.height + 16}
          textAnchor="middle"
          fontFamily="'Atkinson Hyperlegible', system-ui, sans-serif"
          fontSize={9}
          fontWeight={400}
          fill={baselineColor}
          letterSpacing={2.5}
        >
          L&apos;ART À VOIX BASSE
        </text>
      )}
    </svg>
  )
}

export default LumeneLogo
